import React from "react";
import EngineeringIcon from "../EngineeringIcons";
import AnimatedDivider from "../AnimatedDivider";

const PlumbingEngineering: React.FC = () => {
  const plumbingSystems = [
    {
      title: "Industrial Plumbing",
      description: "Comprehensive plumbing systems for industrial facilities and manufacturing plants.",
      icon: "🏭",
      features: ["Process water systems", "Waste management", "Safety systems"]
    },
    {
      title: "Commercial Plumbing",
      description: "Advanced plumbing solutions for commercial buildings and business facilities.",
      icon: "🏢",
      features: ["HVAC integration", "Water conservation", "Maintenance systems"]
    },
    {
      title: "Residential Plumbing",
      description: "Modern plumbing systems for residential buildings and housing developments.",
      icon: "🏠",
      features: ["Smart home integration", "Water filtration", "Energy efficiency"]
    },
    {
      title: "Sustainable Water Management",
      description: "Eco-friendly water management systems that promote conservation and efficiency.",
      icon: "💧",
      features: ["Rainwater harvesting", "Greywater recycling", "Water treatment"]
    }
  ];

  const services = [
    "Plumbing system design and engineering",
    "Installation and commissioning",
    "Maintenance and repair services",
    "Water treatment systems",
    "Emergency plumbing services",
    "System upgrades and modernization"
  ];

  const benefits = [
    {
      title: "Reliability",
      description: "Robust systems designed for long-term performance and reliability",
      icon: "🛡️"
    },
    {
      title: "Efficiency",
      description: "Optimized systems that reduce water waste and energy consumption",
      icon: "⚡"
    },
    {
      title: "Sustainability",
      description: "Eco-friendly solutions that promote water conservation",
      icon: "🌱"
    },
    {
      title: "Safety",
      description: "Comprehensive safety systems that protect people and property",
      icon: "🔒"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-teal-600 to-teal-700 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="animate-pulse">
                <EngineeringIcon type="chain" size="lg" className="text-white" />
              </div>
            </div>
            <h1 className="text-5xl font-bold mb-6">Plumbing Engineering</h1>
            <p className="text-xl mb-8">
              Comprehensive plumbing systems design and installation. Industrial, commercial, and residential 
              plumbing solutions with sustainable water management.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-white/20 px-4 py-2 rounded-full">Infrastructure Excellence</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Water Management</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Sustainable Solutions</span>
            </div>
          </div>
        </div>
      </section>

      <AnimatedDivider variant="gears" />

      {/* Plumbing Systems */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Plumbing Solutions</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive plumbing engineering solutions designed for efficiency, reliability, and sustainability.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {plumbingSystems.map((system, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8 engineering-card">
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4 animate-bounce-slow">{system.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-800">{system.title}</h3>
                </div>
                <p className="text-gray-600 mb-6">{system.description}</p>
                <ul className="space-y-2">
                  {system.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-700">
                      <EngineeringIcon type="bolt" size="sm" className="text-teal-500 mr-3" animated={false} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="tools" />

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why Choose Our Plumbing Engineering?</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our plumbing solutions deliver exceptional benefits for your infrastructure needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg hover:bg-teal-50 transition-colors">
                <div className="text-4xl mb-4 animate-pulley-bounce">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="mixed" />

      {/* Services */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive plumbing engineering services to meet all your infrastructure needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center">
                  <EngineeringIcon type="wrench" size="md" className="text-teal-500 mr-4" animated={false} />
                  <span className="font-semibold text-gray-800">{service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Build Reliable Infrastructure</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Experience world-class plumbing engineering solutions. Let's build efficient, 
            sustainable, and reliable water management systems for Ekumfiman.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="chain" size="sm" animated={false} />
              Get a Quote
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-teal-600 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="gear" size="sm" />
              Learn More
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PlumbingEngineering; 